import {
  GoogleAuthProvider,
  signInWithPopup,
  linkWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  signInAnonymously,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth'
import { auth } from './firebase'

const googleProvider = new GoogleAuthProvider()

/**
 * Resolves once Firebase has restored any persisted session. Until it fires,
 * auth.currentUser is null even for a signed-in user, so anything that acts on
 * "no current user" must wait for this first.
 */
let authRestored: Promise<User | null> | null = null
function whenAuthRestored(): Promise<User | null> {
  if (!authRestored) {
    authRestored = new Promise((resolve) => {
      const unsub = onAuthStateChanged(auth, (u) => {
        unsub()
        resolve(u)
      })
    })
  }
  return authRestored
}

/**
 * Every participant needs a Firebase identity so Firestore rules can scope reads
 * and writes to the sessions they actually belong to. Joiners get an anonymous one,
 * so joining a room takes a code and a password rather than a signup. Starting a
 * session is the other case: that needs a real account with a verified email.
 */
let anonymousSignIn: Promise<User> | null = null
export async function ensureSignedIn(): Promise<User> {
  const restored = await whenAuthRestored()
  if (restored) return restored
  if (auth.currentUser) return auth.currentUser
  // Shared promise so concurrent callers don't race two anonymous accounts.
  if (!anonymousSignIn) {
    anonymousSignIn = signInAnonymously(auth)
      .then((cred) => cred.user)
      .finally(() => { anonymousSignIn = null })
  }
  return anonymousSignIn
}

/** Authorization header for calls to our Cloud Functions. */
export async function authHeaders(): Promise<Record<string, string>> {
  const user = await ensureSignedIn()
  return { Authorization: `Bearer ${await user.getIdToken()}` }
}

export async function signInWithGoogle() {
  // A visitor who joined someone's room already holds an anonymous account. Upgrading
  // it in place keeps those sessions reachable instead of stranding them on a uid they
  // just signed out of. Falls back to a normal sign-in if the Google account exists.
  const current = auth.currentUser
  if (current?.isAnonymous) {
    try {
      return await linkWithPopup(current, googleProvider)
    } catch (err: any) {
      if (
        err?.code !== 'auth/credential-already-in-use' &&
        err?.code !== 'auth/email-already-in-use'
      ) {
        throw err
      }
    }
  }
  return signInWithPopup(auth, googleProvider)
}

export async function signUpWithEmail(email: string, password: string) {
  const cred = await createUserWithEmailAndPassword(auth, email, password)
  await sendEmailVerification(cred.user)
  return cred
}

export async function signInWithEmail(email: string, password: string) {
  return signInWithEmailAndPassword(auth, email, password)
}

export async function resendEmailVerification(user?: User | null) {
  const target = user || auth.currentUser
  if (target) {
    await sendEmailVerification(target)
  }
}

/**
 * A usable creator account: signed in, and with a confirmed email. Google accounts
 * arrive already confirmed, so only email/password signups have a step to clear.
 */
export function isFullyVerified(user: User | null): boolean {
  if (!user || user.isAnonymous) return false
  const hasGoogle = user.providerData.some((p) => p.providerId === 'google.com')
  return user.emailVerified || hasGoogle
}

export function onAuthChange(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, callback)
}

export async function signOut() {
  return firebaseSignOut(auth)
}

export { auth }
export type { User }
